/**
 * 拓扑 DOT 语法解析器
 *
 * 语法示例:
 *   bus -> breaker -> transformer -> elink(1)
 *   bus -> elink(3) -> eblock(1),eblock(2)
 *   bus -> elink(8) -> eblock*100
 */

const NODE_RE = /^(\w+)(?:\(([^)]*)\))?(?:\*(\d+))?$/i

/** dot 关键字 -> Topology Constructors 键名 */
export const DOT_TYPE_MAP = {
  bus: 'bus',
  breaker: 'breaker',
  transformer: 'transformer',
  pcs: 'pcs',
  elink: 'eLink',
  eblock: 'eBlock',
  load: 'payload',
  pcc: 'pcc',
}

const DEFAULT_LABELS = {
  bus: '母线',
  breaker: '断路器',
  transformer: '变压器',
  pcs: '逆变器',
  elink: '能量链',
  eblock: '能量块',
  load: '负载',
  pcc: '电表',
}

export { DEFAULT_LABELS }

/**
 * @typedef {object} DotNodeToken
 * @property {string} type 小写类型名
 * @property {string|null} param 括号参数
 * @property {number} count 数量（*N 或 1）
 * @property {string} key 节点唯一标识
 * @property {string} raw 原始文本
 */

export class DotParser {
  /**
   * @param {string} dot
   * @returns {DotNodeToken[][]} 每条路径为若干「步骤」，每步可含多个并列节点
   */
  parse(dot) {
    if (!dot || typeof dot !== 'string') {
      return []
    }

    return dot
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line && !line.startsWith('#') && !line.startsWith('//'))
      .map((line) => this.parseLine(line))
      .filter((steps) => steps.length > 0)
  }

  /**
   * @param {string} line
   * @returns {DotNodeToken[][]}
   */
  parseLine(line) {
    return line
      .split('->')
      .map((segment) => segment.trim())
      .filter(Boolean)
      .map((segment) => this.parseSegment(segment))
  }

  /**
   * @param {string} segment 如 "elink(1),eblock(2)" 或 "eblock*100"
   * @returns {DotNodeToken[]}
   */
  parseSegment(segment) {
    return segment
      .split(/[,，\s]+/)
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => this.parseNode(part))
  }

  /**
   * @param {string} text
   * @returns {DotNodeToken}
   */
  parseNode(text) {
    const match = NODE_RE.exec(text)
    if (!match) {
      throw new SyntaxError(`无法解析节点: ${text}`)
    }

    const type = match[1].toLowerCase()
    if (!(type in DOT_TYPE_MAP)) {
      throw new SyntaxError(`未知节点类型: ${type}`)
    }

    const param = match[2] != null && match[2] !== '' ? match[2].trim() : null
    const count = match[3] ? Math.max(1, parseInt(match[3], 10)) : 1

    return {
      type,
      param,
      count,
      key: this.buildKey(type, param, count),
      raw: text,
    }
  }

  /**
   * @param {string} type
   * @param {string|null} param
   * @param {number} count
   */
  buildKey(type, param, count) {
    if (count > 1 && !param) {
      return `${type}:*:${count}`
    }
    if (param != null) {
      return `${type}:${param}`
    }
    return type
  }

  /**
   * 将批量节点展开为独立 key 列表
   * @param {DotNodeToken} token
   * @returns {string[]}
   */
  expandKeys(token) {
    if (token.count > 1 && !token.param) {
      return Array.from({ length: token.count }, (_, i) => `${token.type}:*:${token.count}:${i}`)
    }
    return [token.key]
  }

  /**
   * @param {DotNodeToken} token
   */
  getLabel(token) {
    if (token.param) {
      if (token.type === 'elink') return `${token.param}#eLink`
      if (token.type === 'eblock') return `${token.param}#eBlock`
      return token.param
    }
    return DEFAULT_LABELS[token.type] || token.type
  }
}
